import { all, call, fork, put, takeEvery, take, cancel } from 'redux-saga/effects';
import { loginUserError, 
         loginUserRequest, 
         loginUserRequestOkta, 
         loginUserSuccess, 
         updatePassword, 
         updatePasswordError, 
         updatePasswordSuccess, 
         mfaAuthenticate, 
         mfaSuccess, 
         mfaFail,
         oktaTokenRequestAction,
         oktaTokenSuccess,
         oktaTokenFailure,
         sessionExpired } from './actions';
import { tryLogin, tryLoginOKTA, updatePasswordApi, logout, getMfaQRCode, verifyMfaCode, oktaTokenRequest, oktaRefreshTokenRequest } from '../utils/api/loginApi';
import { LoginActionTypes } from './types';
import { LoginStorageModel, LoginOktaStorageModel } from './model';
import { getStorage } from '../utils/storage/storage.manager';
import { setCurrentUserInfo } from '../shared/actions';
import { SagaIterator } from 'redux-saga';
import { navigateTo } from '../case/actions';
import { environment } from '../../environment/environment';
import { delay } from 'redux-saga/effects';

function* handleOktaTokenRequest(action: ReturnType<typeof oktaTokenRequestAction>): SagaIterator {
  try {
      const storageProvider = getStorage();
      const storageInstance = storageProvider.getInstance(LoginStorageModel);
      yield call(() => storageInstance.clear());

      const storageInstanceForOkta = storageProvider.getInstance(LoginOktaStorageModel);
      yield call(() => storageInstanceForOkta.clear());

      const res = yield call(oktaTokenRequest, action.payload.code, action.payload.codeVerifier, action.payload.nonce);

      if (res.data) {
        yield put(oktaTokenSuccess(res.data));
        yield put(loginUserRequestOkta(res.data.accessToken, res.data.refreshToken, res.data.expiresIn));
      } else {
        yield put(oktaTokenFailure('Failed to handle Okta token request.'));
      }
  } catch (err) {
    if (err instanceof Error) {
      yield put(oktaTokenFailure(`${err.message}\n${err.name}\n${err.stack}`));
    } else {
      yield put(oktaTokenFailure('An unknown error occured.'));
    }
  }
}
export function* handleOktaTokenRefresh(): SagaIterator {
  while (true) {
    try {
      if (!environment.useOkta) return;

      const storage = getStorage()
        .getInstance(LoginOktaStorageModel);

      const stored = yield call(() => storage.find(() => true));
      if (!stored?.oktaRefreshToken || !stored.oktaTokenExpiresAt) {
        return;
      }

      const msUntilExpiry = stored.oktaTokenExpiresAt - Date.now();
      const refreshInMs = Math.max(msUntilExpiry - 60_000, 0);

      yield delay(refreshInMs);

      const current = yield call(() => storage.find(() => true));
      if (!current?.oktaRefreshToken) return;

      const res = yield call(
        oktaRefreshTokenRequest,
        current.oktaRefreshToken
      );

      if (!res.data?.accessToken) {
        yield put(sessionExpired()); // only if refresh token itself is dead
        return;
      }

      const newExpiresAt = Date.now() + (res.data.expiresIn ?? 3600) * 1000;

      yield call(() =>
        storage.upsertOne(() => true, {
          ...current,
          oktaToken: res.data.accessToken,
          oktaRefreshToken:
            res.data.refreshToken ?? current.oktaRefreshToken,
          oktaTokenExpiresAt: newExpiresAt,
        })
      );
    } catch {
      yield put(sessionExpired());
      return;
    }
  }
}

function* handleMfaAuthenticate(action: ReturnType<typeof mfaAuthenticate>): SagaIterator {
  try {
    const storageProvider = getStorage();
    const storageInstance = storageProvider.getInstance(LoginStorageModel);
    yield call(() => storageInstance.clear());

    const res = yield call(verifyMfaCode, action.payload.username, action.payload.mfaCode);

    if (res.data && res.data.refreshToken !== '') {
      yield put(mfaSuccess(res.data));
      yield put(setCurrentUserInfo(
        res.data.languageCode,
        res.data.username,
        res.data.firstName,
        res.data.lastName,
        res.data.parentPartyName,
        res.data.parentPartyDibCode,
        res.data.parentPartyId,
        res.data.useMfa,
        res.data.roles,
        res.data.mfaQRCode,
        res.data.registeredForMfa));
    } else {
      if (res.data.refreshToken === '') {
        // There is no refresh token, this means the mfa code was incorrect
        yield put(mfaFail('Incorrect MFA code'));
      }
    }
    if (res.response && res.response.data.error) {
      yield put(mfaFail(res.response.data.error.message));
    }
  } catch (err) {
    if (err instanceof Error) {
      yield put(mfaFail(`${err.message}\n${err.name}\n${err.stack}`));
    } else {
      yield put(mfaFail('An unknown error occured.'));
    }
  }
}

function* handleMfaSuccess(action: ReturnType<typeof mfaSuccess>): SagaIterator {
  const storageProvider = getStorage();
  const storageInstance = storageProvider.getInstance(LoginStorageModel);
  const model = new LoginStorageModel();

  model.id = action.payload.data.id;
  model.username = action.payload.data.username;
  model.firstName = action.payload.data.firstName;
  model.lastName = action.payload.data.lastName;
  model.token = action.payload.data.token;
  model.roles = action.payload.data.roles;
  model.expires_in = action.payload.data.expires_in;
  model.expires_on = action.payload.data.expires_on;
  model.refreshToken = action.payload.data.refreshToken;
  model.refresh_token_expires_in = action.payload.data.refresh_token_expires_in;
  model.refresh_token_expires_on = action.payload.data.refresh_token_expires_on;
  model.languageCode = action.payload.data.languageCode;
  model.parentPartyName = action.payload.data.parentPartyName;
  model.parentPartyDibCode = action.payload.data.parentPartyDibCode;
  model.parentPartyId = action.payload.data.parentPartyId;
  model.useMfa = action.payload.data.useMfa;
  model.registeredForMfa = action.payload.data.registeredForMfa;
  model.mfaQRCode = action.payload.data.mfaQRCode;

  yield call(() => storageInstance.upsertOne(x => x.id === model.id, model));

  yield put(navigateTo('/'));
}

function* handleMfaFail(action: ReturnType<typeof mfaFail>): SagaIterator {
  // alert or some other side effect?

  // clear login/token data
  const storageProvider = getStorage();
  const storageInstance = storageProvider.getInstance(LoginStorageModel);
  yield call(() => storageInstance.clear());

  const storageInstanceForOkta = storageProvider.getInstance(LoginOktaStorageModel);
  yield call(() => storageInstanceForOkta.clear());
}

function* handleLoginRequest(action: ReturnType<typeof loginUserRequest>): SagaIterator {
  try {
    const storageProvider = getStorage();
    const storageInstance = storageProvider.getInstance(LoginStorageModel);
    yield call(() => storageInstance.clear());

    const storageInstanceForOkta = storageProvider.getInstance(LoginOktaStorageModel);
    yield call(() => storageInstanceForOkta.clear());

    const res = yield call(tryLogin, action.payload.username, action.payload.password);

    if (res.data) {
      if (res.data.useMfa && !res.data.registeredForMfa) {  // We check if the user needs 2FA and he has not yet registered his device

        const result = yield call(getMfaQRCode, res.data.username);
        res.data.mfaQRCode = 'data:image/png;base64,' + result.data;
      } else {
        res.data.mfaQRCode = '';
      }

      yield put(loginUserSuccess(res.data, false));
      yield put(setCurrentUserInfo(
        res.data.languageCode,
        res.data.username,
        res.data.firstName,
        res.data.lastName,
        res.data.parentPartyName,
        res.data.parentPartyDibCode,
        res.data.parentPartyId,
        res.data.useMfa,
        res.data.roles,
        res.data.mfaQRCode,
        res.data.registeredForMfa));
    }
    if (res.response && res.response.data.error) {
      yield put(loginUserError(res.response.data.error.message));
    }
  } catch (err) {
    if (err instanceof Error) {
      yield put(loginUserError(`${err.message}\n${err.name}\n${err.stack}`));
    } else {
      yield put(loginUserError('An unknown error occured.'));
    }
  }
}

function* handleLoginRequestOKTA(action: ReturnType<typeof loginUserRequestOkta>): SagaIterator {
  try {
    const storageProvider = getStorage();
    const storageInstance = storageProvider.getInstance(LoginStorageModel);
    yield call(() => storageInstance.clear());

    const storageInstanceForOkta = storageProvider.getInstance(LoginOktaStorageModel);
    yield call(() => storageInstanceForOkta.clear());

    const res = yield call(tryLoginOKTA, action.payload.access_token);

    if (res.data) {
      const model = new LoginOktaStorageModel();
      model.oktaToken = action.payload.access_token;
      model.oktaRefreshToken = action.payload.refresh_token;
      model.oktaTokenExpiresAt = Date.now() + (action.payload.expires_in * 1000);
      yield call(() => storageInstanceForOkta.addOne(model));

      yield put({ type: LoginActionTypes.OKTA_SESSION_STARTED });
      yield put(loginUserSuccess(res.data, true));
      yield put(setCurrentUserInfo(
        res.data.languageCode,
        res.data.username,
        res.data.firstName,
        res.data.lastName,
        res.data.parentPartyName,
        res.data.parentPartyDibCode,
        res.data.parentPartyId,
        res.data.useMfa,
        res.data.roles,
        res.data.mfaQRCode,
        res.data.registeredForMfa));
    }
    if (res.response && res.response.data.error) {
      yield put(loginUserError(res.response.data.error.message));
    }
  } catch (err) {
    if (err instanceof Error) {
      yield put(loginUserError(`${err.message}\n${err.name}\n${err.stack}`));
    } else {
      yield put(loginUserError('An unknown error occured.'));
    }
  }
}

function* handleLoginSuccess(action: ReturnType<typeof loginUserSuccess>): SagaIterator {
  const storageProvider = getStorage();
  const storageInstance = storageProvider.getInstance(LoginStorageModel);
  const model = new LoginStorageModel();

  model.id = action.payload.data.id;
  model.username = action.payload.data.username;
  model.firstName = action.payload.data.firstName;
  model.lastName = action.payload.data.lastName;
  model.token = action.payload.data.token;
  model.roles = action.payload.data.roles;
  model.expires_in = action.payload.data.expires_in;
  model.expires_on = action.payload.data.expires_on;
  model.refreshToken = action.payload.data.refreshToken;
  model.refresh_token_expires_in = action.payload.data.refresh_token_expires_in;
  model.refresh_token_expires_on = action.payload.data.refresh_token_expires_on;
  model.languageCode = action.payload.data.languageCode;
  model.parentPartyName = action.payload.data.parentPartyName;
  model.parentPartyDibCode = action.payload.data.parentPartyDibCode;
  model.parentPartyId = action.payload.data.parentPartyId;
  model.useMfa = action.payload.data.useMfa;
  model.registeredForMfa = action.payload.data.registeredForMfa;
  model.mfaQRCode = action.payload.data.mfaQRCode;
  model.isSilent = action.payload.data.isSilent;

  yield call(() => storageInstance.upsertOne(x => x.id === model.id, model));

  if (model.useMfa) {
    navigateTo('/mfaauthenticate');
  } 
  else {
    // only redirect if user really came from login
    if (!model.isSilent) {
      yield put(navigateTo('/'));
    }

  }
}

function* handleLoginFail(action: ReturnType<typeof loginUserError>): SagaIterator {
  // alert or some other side effect?

  // clear login/token data
  const storageProvider = getStorage();
  const storageInstance = storageProvider.getInstance(LoginStorageModel);
  yield call(() => storageInstance.clear());

  const storageInstanceForOkta = storageProvider.getInstance(LoginOktaStorageModel);
  yield call(() => storageInstanceForOkta.clear());
}

function* handleLogout(): SagaIterator {
  const res = yield call(logout);

  if (res.data) {
    
    // Clear local app storage
    const storageProvider = getStorage();
    storageProvider.getInstance(LoginStorageModel).clear();
    storageProvider.getInstance(LoginOktaStorageModel).clear();

    // If Okta is not used → normal redirect
    if (!environment.useOkta) {
      yield put(navigateTo('/login'));
      return;
    }

    // --- OKTA LOGOUT FLOW ---
    // Clear PKCE values just in case
    sessionStorage.removeItem('pkce_code_verifier');
    sessionStorage.removeItem('pkce_state');
    sessionStorage.removeItem('pkce_nonce');

    const redirectUri = `${window.location.origin}/login?loggedOut=true`;

    const oktaLogoutUrl =
      `${environment.oktaLogoutUrl}?` +
      `post_logout_redirect_uri=${encodeURIComponent(redirectUri)}`;

    window.location.replace(oktaLogoutUrl);
  }
}

function* handleChangePassword(): SagaIterator {
  yield put(navigateTo('/changePassword'));
}

function* handleUpdatePassword(action: ReturnType<typeof updatePassword>): SagaIterator {

  if (action.payload.newPassword !== action.payload.confirmNewPassword) {
    yield put(updatePasswordError('changePassword.confirmPasswordNotOKMessage'));
  } else {
    try {
      const res = yield call(updatePasswordApi, {currentPassword: action.payload.currentPassword, newPassword: action.payload.newPassword });
      if (res.data) {
        if (res.data.success) {
          yield put(updatePasswordSuccess());

          yield put(navigateTo('/'));
        } else {
          yield put(updatePasswordError(res.data.message));
        }
      }
      if (res.response && res.response.data.error) {
        yield put(updatePasswordError(res.response.data.error.message));
      }
    } catch (err) {
      if (err instanceof Error) {
        yield put(updatePasswordError(`${err.message}\n${err.name}\n${err.stack}`));
      } else {
        yield put(updatePasswordError('An unknown error occured.'));
      }
    }
  }
}
// This is our watcher function. We use `take*()` functions to watch Redux for a specific action
// type, and run our saga, for example the `handleFetch()` saga above.
function* watchLogin() {
  yield takeEvery(LoginActionTypes.LOG_IN_REQUEST, handleLoginRequest);
  yield takeEvery(LoginActionTypes.LOG_IN_REQUEST_OKTA, handleLoginRequestOKTA);
  yield takeEvery(LoginActionTypes.MFA_AUTHENTICATE, handleMfaAuthenticate);
  yield takeEvery(LoginActionTypes.MFA_SUCCESS, handleMfaSuccess);
  yield takeEvery(LoginActionTypes.MFA_FAIL, handleMfaFail);
  yield takeEvery(LoginActionTypes.LOG_IN_SUCCESS, handleLoginSuccess);
  yield takeEvery(LoginActionTypes.LOG_IN_FAIL, handleLoginFail);
  yield takeEvery(LoginActionTypes.LOGOUT, handleLogout);
  yield takeEvery(LoginActionTypes.CHANGE_PASSWORD, handleChangePassword);
  yield takeEvery(LoginActionTypes.UPDATE_PASSWORD, handleUpdatePassword);
  yield takeEvery(LoginActionTypes.OKTA_TOKEN_REQUEST, handleOktaTokenRequest);
}
function* watchOktaRefresh(): SagaIterator {
  while (true) {
    yield take(LoginActionTypes.LOG_IN_SUCCESS);

    const task = yield fork(handleOktaTokenRefresh);

    yield take(LoginActionTypes.LOGOUT);
    yield cancel(task);
  }
}
// We can also use `fork()` here to split our saga into multiple watchers.
function* loginSaga() {
  yield all([
    fork(watchLogin),
  ]);
}

export default loginSaga;
